import {
  changeEsignatureCredentialSigningCredentialChangePost,
  completeEsignatureCredentialResetSigningCredentialResetPost,
  getEsignatureCredentialStatusSigningCredentialGet,
  requestEsignatureCredentialResetSigningCredentialResetRequestPost,
  setEsignatureCredentialSigningCredentialPut,
} from '../../services/api/sdk.gen';

/**
 * Minimum e-signature password length.
 *
 * Mirrors MIN_PASSWORD_LENGTH in backend/app/services/esignature_credential.py so
 * a too-short password is caught before the request instead of coming back as a
 * 422 after the dialog has already closed.
 */
export const MIN_ESIGN_PASSWORD_LENGTH = 4;

/** Whether the signed-in user has an e-signature password, and any lockout. */
export interface ESignatureCredentialStatus {
  has_credential: boolean;
  locked_until: string | null;
  /**
   * Today's date where the signature is being taken, as `YYYY-MM-DD`, computed
   * server-side.
   *
   * The signing dialog seeds its date picker from this rather than reading the
   * browser clock: a clinician working outside the signing zone would otherwise
   * be offered a date the server disagrees with, and neither the clinic nor the
   * agency timezone is available on the client at all.
   *
   * Resolved through the patient's clinic when `fetchESignatureCredentialStatus`
   * is given a patient, falling back to the agency. That distinction is not
   * cosmetic: the agency timezone has no write path, so every agency reads as
   * Pacific, and an Eastern clinic would be offered yesterday's date all evening.
   */
  default_signature_date: string;
}

/**
 * The e-signature password a signing request presents.
 *
 * Shaped to match the wire body so panels can spread it straight into their
 * existing sign / approve request alongside `attestation_version`. Exactly one
 * field is set: `new_esign_password` on a clinician's first-ever signature,
 * `esign_password` every time after that.
 */
export interface ESignatureCredentialInput {
  esign_password?: string;
  new_esign_password?: string;
}

/** The date a signer declared, as the wire expects it (`YYYY-MM-DD`). */
export interface SignatureDateInput {
  signature_date: string;
}

export async function fetchESignatureCredentialStatus(
  patientId?: string
): Promise<ESignatureCredentialStatus> {
  const { data } = await getEsignatureCredentialStatusSigningCredentialGet({
    query: patientId ? { patient_id: patientId } : undefined,
    throwOnError: true,
  });
  return {
    has_credential: Boolean(data?.has_credential),
    locked_until: data?.locked_until ?? null,
    default_signature_date: data?.default_signature_date ?? '',
  };
}

export async function createESignatureCredential(
  newPassword: string
): Promise<void> {
  await setEsignatureCredentialSigningCredentialPut({
    body: { new_password: newPassword },
    throwOnError: true,
  });
}

export async function changeESignatureCredential(
  currentPassword: string,
  newPassword: string
): Promise<void> {
  await changeEsignatureCredentialSigningCredentialChangePost({
    body: { current_password: currentPassword, new_password: newPassword },
    throwOnError: true,
  });
}

/** Email the signed-in user a single-use code to reset their password. */
export async function requestESignatureCredentialReset(): Promise<void> {
  await requestEsignatureCredentialResetSigningCredentialResetRequestPost({
    throwOnError: true,
  });
}

export async function completeESignatureCredentialReset(
  code: string,
  newPassword: string
): Promise<void> {
  await completeEsignatureCredentialResetSigningCredentialResetPost({
    body: { code, new_password: newPassword },
    throwOnError: true,
  });
}
